/**
 * ## Availability of reservations inventory
 *
 * @module src/controllers/reservation
 */

"use strict";

const moment = require("moment-timezone");
const conf = require("../../config/availability.js");

const prePop = (slot, date) => {
  const description = moment(date * 1000 + slot * 15 * 60 * 1000).format(
    "h:mm a"
  );
  return {
    time: slot,
    description,
    limitIndex: null,
    filled: 0,
    seats: 0,
    parties: 0,
    available: false,
  };
};

/**
 * This returns tables that are occupied by reservations during the provided date
 */
const getPreSelect = (kd, date, time, reservation_id) => {
  // get location
  let location = Object.assign({}, kd.doc("location")[0]);
  // get active sheet to get turn time range

  let reservations = Object.assign(
    [],
    kd.where(
      "reservation",
      (obj) => {
        if (
          obj._id !== reservation_id &&
          obj.date === date &&
          obj.status === 1 &&
          obj.requireConfirmation === 0 &&
          obj.time <= time + location.baseTurn / 15 &&
          obj.time >= time - location.baseTurn / 15
        ) {
          return true;
        }
      },
      "time"
    )
  );
  let tables = [];
  for (let i = 0; i < reservations.length; ++i) {
    if (
      reservations[i].hasOwnProperty("preSelect") &&
      reservations[i].preSelect.length
    ) {
      tables = tables.concat(reservations[i].preSelect);
    }
  }
  return tables;
};

/** byDate
 */
const byDate = (kd, date) => {
  // check if there is date limit defined to date

  const matrix = {
    slots: [],
  };
  let reservations = [];

  matrix.limit = kd.where(
    "limit",
    (obj) => {
      if (
        obj.active === 1 &&
        obj.type === "date" &&
        obj.range.start <= date &&
        obj.range.end - 1 >= date
      ) {
        return true;
      }
    },
    "time"
  );

  if (matrix.limit.length === 0) {
    const day = parseInt(moment(date * 1000).day(), 10);
    matrix.limit = kd.where(
      "limit",
      (obj) => {
        if (
          obj.active === 1 &&
          obj.type === "day" &&
          obj.range[conf.daysOfWeek[day]] === 1
        ) {
          return true;
        }
      },
      "time"
    );
  }

  if (matrix.limit.length === 0) {
    // no limit covering this date
    for (let i = 0; i < 96; ++i) {
      matrix.slots[i] = prePop(i, date);
    }
  } else {
    let limitIndex = 0;
    let closed = false;
    matrix.location = Object.assign({}, kd.doc("location")[0]);

    if (
      date < matrix.location.operation.openDate ||
      date > matrix.location.operation.closeDate
    ) {
      closed = true;
    }

    reservations = Object.assign(
      [],
      kd.where(
        "reservation",
        (obj) => {
          if (
            obj.date === date &&
            obj.status === 1 &&
            obj.requireConfirmation === 0
          ) {
            return true;
          }
        },
        "time"
      )
    );

    for (let i = 0; i < 96; ++i) {
      matrix.slots[i] = prePop(i, date);
      if (!closed) {
        if (i >= matrix.limit[0].open && i < matrix.limit[0].close) {
          for (let j = 0; j < matrix.limit[0].limits.length; ++j) {
            if (matrix.limit[0].limits[j].start == i) {
              limitIndex = j;
              break;
            }
          }
          matrix.slots[i].limitIndex = limitIndex;
          matrix.slots[i].seats = Math.floor(
            (matrix.limit[0].limits[limitIndex].split / 100) *
              matrix.limit[0].seats
          );
          for (let l = 0; l < reservations.length; ++l) {
            let size = reservations[l].size;
            if (size > 14) {
              size = 14;
            }
            if (
              reservations[l].time <= i &&
              reservations[l].time +
                Math.ceil(
                  matrix.limit[0].limits[limitIndex].turnTime +
                    matrix.location.turnTimes[Math.ceil(size / 2) - 1].turnTime
                ) /
                  15 >
                i
            ) {
              ++matrix.slots[i].parties;
              matrix.slots[i].filled += parseInt(reservations[l].size, 10);
            }
          }
        }

        if (matrix.slots[i].seats > matrix.slots[i].filled) {
          matrix.slots[i].available = true;
        }
      }
    }
  }

  return matrix;
};

const byMonth = (kd, date) => {
  const firstDay = parseInt(moment(date).startOf("month").format("X"), 10);
  const lastDay = parseInt(
    moment(firstDay * 1000)
      .endOf("month")
      .format("X"),
    10
  );
  const month = moment(date).format("MM");
  const year = moment(date).format("YYYY");
  const daysInMonth = parseInt(moment(date).daysInMonth(), 10);

  const matrix = {};

  for (let day = 1; day <= daysInMonth; ++day) {
    const dayf = `0${day}`.slice(-2);
    const i = moment(`${year}-${month}-${dayf}`).startOf("day").format("X");
    matrix[i] = {
      claimed: 0,
      total: 0,
    };
    let limit = kd.where(
      "limit",
      (obj) => {
        const start = parseInt(
          moment(firstDay * 1000)
            .add(day - 1, "days")
            .format("X"),
          10
        );
        const end = parseInt(
          moment(firstDay * 1000)
            .add(day, "days")
            .format("X"),
          10
        );
        if (
          obj.active === 1 &&
          obj.type === "date" &&
          obj.range.start >= start &&
          obj.range.end - 1 <= end
        ) {
          return true;
        }
      },
      "time"
    );
    if (limit.length === 0) {
      const day2 = parseInt(moment(i * 1000).day(), 10);
      limit = kd.where(
        "limit",
        (obj) => {
          if (
            obj.active === 1 &&
            obj.type === "day" &&
            obj.range[conf.daysOfWeek[day2]] === 1
          ) {
            return true;
          }
        },
        "time"
      );
    }
    if (limit.length > 0) {
      for (let t = 0; t < limit[0].limits.length; ++t) {
        matrix[i].total += Math.ceil(
          (limit[0].limits[t].split / 100) *
            limit[0].seats *
            (((limit[0].limits[t].end - limit[0].limits[t].start) * 15) /
              limit[0].limits[t].turnTime)
        );
      }
    }
  }

  const reservations = kd.where("reservation", (obj) => {
    if (
      obj.date >= firstDay &&
      obj.date <= lastDay &&
      obj.status === 1 &&
      obj.requireConfirmation === 0
    ) {
      return true;
    }
  });
  for (let r = 0; r < reservations.length; ++r) {
    if (matrix.hasOwnProperty(reservations[r].date)) {
      matrix[reservations[r].date].claimed += reservations[r].size;
    }
  }
  return matrix;
};

const check = (kd, date, time, size) => {
  const matrix = byDate(kd, date);
  let thisSize = size;
  if (!size) {
    thisSize = 2;
  }
  if (thisSize > 14) {
    thisSize = 14;
  }
  let span = 0;
  if (matrix.slots[time].limitIndex !== null) {
    span = Math.ceil(
      (parseInt(
        matrix.limit[0].limits[matrix.slots[time].limitIndex].turnTime,
        10
      ) +
        parseInt(
          matrix.location.turnTimes[Math.ceil(thisSize / 2) - 1].turnTime,
          10
        )) /
        15
    );
    let open = true;
    if (matrix) {
      for (let i = time; i < time + span; ++i) {
        if (
          matrix.slots[i].available &&
          matrix.limit[0].limits.length - 1 == matrix.slots[i].limitIndex &&
          matrix.slots[i].seats - matrix.slots[i].filled > thisSize
        ) {
          // final limit block, only check first time for availability to let
          // them run over.
          return true;
        } else if (
          !matrix.slots[i].available ||
          matrix.slots[i].seats - matrix.slots[i].filled < thisSize
        ) {
          open = false;
          break;
        }
      }
      return open;
    }
    return false;
  }
  return false;
};

module.exports = {
  byDate,
  byMonth,
  check,
  getPreSelect,
};



/** WEBPACK FOOTER **
 ** ./src/common/availability/index.js
 **/